import { useCallback } from 'react';
import { map } from 'lodash';
import { Typography, Stack } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { MultipleSelect } from '@client/components/forms/Select/MultipleSelect';
import { MenuItem } from '@client/components/forms/Select/MenuItem';
import { useDispatch, useListeners, useSelector } from '@client/hooks/redux.hooks';
import { Select } from '@client/components/forms/Select';
import {
  getDisabilities, getGenders, getIdentities, getSexualities,
} from '@client/selectors/entities/categories';
import { FormStack } from '@client/components/forms/FormStack';
import { newsUrl } from '@sda/utils/url';
import { Tip } from '@client/components/common/Tip';
import { getSelectedProfile } from '@client/selectors/helpers/profile';
import { ProfileTypes } from '@sda/enums';
import { SignupWrapperForm } from '../../common/SignupWrapper/SignupWrapperForm';
import { signupArtistIdentificationListeneners, startSaveArtistIdentification } from './signup-artist-identification.listeners';
import { SignupArtistIdentificationSchema } from './signup-artist-identification.schema';

const useStyles = makeStyles()((theme) => ({
  tipContainer: {
    textAlign: 'left',

    '& > p': {
      marginBottom: theme.spacing(1),

      [theme.breakpoints.up('md')]: {
        marginBottom: theme.spacing(2),
      },
    },
  },
}));

const Explainer = () => {
  const { classes } = useStyles();

  return (
    <Stack spacing={2}>
      <Typography
        variant="h6"
        align="left"
      >
        Self-identification
      </Typography>

      <div className={classes.tipContainer}>
        <Typography
          color="textSecondary"
          align="left"
        >
          This is optional. We use this data to understand our users and serve them better.
        </Typography>

        <Tip
          id="sda-artist-equity-link"
          label="News:"
          tipText="Side Door's Vision for Equity"
          linkTo={newsUrl('/news/edi-vision')}
        />
      </div>
    </Stack>
  );
};

export const Identification = () => {
  const dispatch = useDispatch();
  const selectedProfile = useSelector(getSelectedProfile);

  const identities = useSelector(getIdentities);
  const genders = useSelector(getGenders);
  const sexualities = useSelector(getSexualities);
  const disabilities = useSelector(getDisabilities);

  useListeners(signupArtistIdentificationListeneners);

  const initialValues = {
    identities: selectedProfile?.profileType === ProfileTypes.ACT ? selectedProfile.identities || [] : [],
    disability: selectedProfile?.profileType === ProfileTypes.ACT ? selectedProfile.disability || '' : '',
    genders: selectedProfile?.profileType === ProfileTypes.ACT ? selectedProfile.genders || [] : [],
    sexualities: selectedProfile?.profileType === ProfileTypes.ACT ? selectedProfile.sexualities || [] : [],
  };

  const handleSubmit = useCallback((values: typeof initialValues) => {
    dispatch(startSaveArtistIdentification(values));
  }, [dispatch]);

  return (
    <SignupWrapperForm
      steps={6}
      currentStep={6}
      renderExplainer={<Explainer />}
      FormikProps={{
        initialValues,
        onSubmit: handleSubmit,
        validationSchema: SignupArtistIdentificationSchema,
      }}
      buttonProps={() => ({
        scope: 'signup-act-identification-form',
      })}
    >
      <FormStack>
        <MultipleSelect
          name="identities"
          label="What is your cultural identity?"
          fullWidth
        >
          {map(identities, (identity) => (
            <MenuItem
              key={identity.id}
              value={identity.id}
              checkbox
            >
              {identity.lang.label}
            </MenuItem>
          ))}
        </MultipleSelect>

        <Select
          name="disability"
          label="Do you identify as disabled?"
          fullWidth
        >
          {map(disabilities, (disability) => (
            <MenuItem
              key={disability.id}
              value={disability.id}
            >
              {disability.lang.label}
            </MenuItem>
          ))}
        </Select>

        <MultipleSelect
          name="sexualities"
          label="What is your sexual orientation?"
          fullWidth
        >
          {map(sexualities, (sexuality) => (
            <MenuItem
              key={sexuality.id}
              value={sexuality.id}
              checkbox
            >
              {sexuality.lang.label}
            </MenuItem>
          ))}
        </MultipleSelect>

        <MultipleSelect
          name="genders"
          label="What is your gender identity?"
          fullWidth
        >
          {map(genders, (gender) => (
            <MenuItem
              key={gender.id}
              value={gender.id}
              checkbox
            >
              {gender.lang.label}
            </MenuItem>
          ))}
        </MultipleSelect>
      </FormStack>
    </SignupWrapperForm>
  );
};
